import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FreeTerminForm } from "@/components/FreeTerminForm";
import { Sun, Flame, CalendarClock, ArrowRight, Plus, FileSignature, User as UserIcon, Wrench as Wrench2, ChevronLeft, ChevronRight, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

const PHASE_STATUS_CLS = {
  offen: "bg-slate-100 text-slate-900 dark:bg-slate-800/60 dark:text-slate-200",
  geplant: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300",
  in_arbeit: "bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-300",
  fertig: "bg-teal-100 text-teal-900 dark:bg-teal-900/30 dark:text-teal-300",
  abgenommen: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300",
};
const PHASE_STATUS_LBL = {
  offen: "Offen", geplant: "Geplant", in_arbeit: "In Arbeit", fertig: "Fertig", abgenommen: "Abgenommen",
};

// Lokales Datum als YYYY-MM-DD — NIEMALS toISOString() nutzen, das shifted in UTC (bewirkt +/-1 Tag).
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Category: PV-DC / PV-AC / WP / Nacharbeiten — used for color-coding calendar dots
export function terminCategory(t) {
  if (t.project_type === "frei") return "frei";
  if (t.phase_key === "nacharbeiten") return "nacharbeiten";
  if (t.project_type === "pv" && t.phase_key === "dc_montage") return "pv_dc";
  if (t.project_type === "pv" && t.phase_key === "ac_montage") return "pv_ac";
  if (t.project_type === "pv") return "pv_dc"; // fallback for any other pv phase
  return "wp";
}

export const CATEGORY_META = {
  pv_dc: { color: "bg-emerald-500", label: "PV · DC (Dach)", icon: Sun },
  pv_ac: { color: "bg-red-500", label: "PV · AC (Elektro)", icon: Sun },
  wp: { color: "bg-purple-500", label: "Wärmepumpe", icon: Flame },
  nacharbeiten: { color: "bg-orange-500", label: "Nacharbeiten", icon: Wrench2 },
  frei: { color: "bg-sky-500", label: "Sonstige Termine", icon: CalendarClock },
};

const ALL_CATS = ["pv_dc", "pv_ac", "wp", "nacharbeiten", "frei"];

// Freie Termine als Pseudo-Projekte, damit Kalender & Konfliktprüfung sie gleich behandeln
export function freeTermineAsProjects(list) {
  return (list || []).map((f) => ({
    id: `frei-${f.id}`, freiId: f.id, project_type: "frei", kunde_name: f.titel, kunde_adresse: f.notiz || "",
    phases: [{ key: "frei", label: "Sonstiger Termin", status: "offen", monteur_id: f.monteur_id || null, termin: f.datum, termin_ende: f.datum_ende || null, uhrzeit_von: f.uhrzeit_von || null, uhrzeit_bis: f.uhrzeit_bis || null }],
  }));
}

// "YYYY-MM-DD" Hilfen (UTC, damit keine Zeitzonen-Verschiebung entsteht)
export function addDaysStr(s, n) {
  const d = new Date(`${s}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
export function diffDaysStr(a, b) {
  return Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000);
}

// Extract flat list of {project, phase, date} from all projects (mehrtägige Termine je Tag)
export function collectProjectTermine(projects) {
  const out = [];
  for (const p of projects || []) {
    for (const ph of p.phases || []) {
      if (ph.termin) {
        const count = ph.termin_ende && ph.termin_ende > ph.termin ? Math.min(diffDaysStr(ph.termin, ph.termin_ende) + 1, 31) : 1;
        for (let i = 0; i < count; i++) {
          out.push({
            projectId: p.id,
            project_type: p.project_type,
            kunde_name: count > 1 ? `${p.kunde_name} (${i + 1}/${count})` : p.kunde_name,
            kunde_plz: p.kunde_plz || "",
            kunde_adresse: p.kunde_adresse,
            phase_key: ph.key,
            phase_label: ph.label,
            status: ph.status,
            monteur_id: ph.monteur_id || null,
            termin: addDaysStr(ph.termin, i), // YYYY-MM-DD (dieser Tag)
            termin_start: ph.termin,
            termin_ende: count > 1 ? ph.termin_ende : null,
            uhrzeit_von: ph.uhrzeit_von || null,
            uhrzeit_bis: ph.uhrzeit_bis || null,
          });
        }
      }
    }
  }
  out.sort((a, b) => a.termin.localeCompare(b.termin) || (a.uhrzeit_von || "99").localeCompare(b.uhrzeit_von || "99"));
  return out;
}

// Build a map { "YYYY-MM-DD": Set("pv","wp","abnahme") } for calendar dots
export function buildCategoryMap(termine) {
  const m = {};
  for (const t of termine) {
    const cat = terminCategory(t);
    if (!m[t.termin]) m[t.termin] = new Set();
    m[t.termin].add(cat);
  }
  return m;
}

const CAT_ORDER = ["pv_dc", "pv_ac", "wp", "nacharbeiten", "frei"];

const CAT_BG = {
  pv_dc: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-900 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700",
  pv_ac: "bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-200 border-red-300 dark:border-red-700",
  nacharbeiten: "bg-orange-100 dark:bg-orange-900/40 text-orange-900 dark:text-orange-200 border-orange-300 dark:border-orange-700",
  wp: "bg-purple-100 dark:bg-purple-900/40 text-purple-900 dark:text-purple-200 border-purple-300 dark:border-purple-700",
  frei: "bg-sky-100 dark:bg-sky-900/40 text-sky-900 dark:text-sky-200 border-sky-300 dark:border-sky-700",
};

export function timeLabel(t) {
  if (!t?.uhrzeit_von) return "";
  return t.uhrzeit_bis ? `${t.uhrzeit_von}–${t.uhrzeit_bis}` : t.uhrzeit_von;
}

// Andere Einsätze desselben Monteurs, die sich mit [start, end] (und ggf. Uhrzeit) überschneiden
export function findMonteurConflicts(projects, monteurId, start, end, excludeProjectId, excludePhaseKey, von, bis) {
  if (!monteurId || !start) return [];
  const e1 = end && end > start ? end : start;
  const out = [];
  for (const p of projects || []) {
    for (const ph of p.phases || []) {
      if (ph.monteur_id !== monteurId || !ph.termin) continue;
      if (p.id === excludeProjectId && ph.key === excludePhaseKey) continue;
      const e2 = ph.termin_ende && ph.termin_ende > ph.termin ? ph.termin_ende : ph.termin;
      if (ph.termin <= e1 && e2 >= start) {
        if (von && bis && ph.uhrzeit_von && ph.uhrzeit_bis && (bis <= ph.uhrzeit_von || von >= ph.uhrzeit_bis)) continue;
        out.push({ projectId: p.id, kunde_name: p.kunde_name, phase_label: ph.label, termin: ph.termin, termin_ende: e2 !== ph.termin ? e2 : null, uhrzeit_von: ph.uhrzeit_von, uhrzeit_bis: ph.uhrzeit_bis });
      }
    }
  }
  return out.sort((a, b) => (a.termin + (a.uhrzeit_von || "99")).localeCompare(b.termin + (b.uhrzeit_von || "99")));
}

export function conflictText(c) {
  return `${c.kunde_name} · ${c.phase_label} (${c.termin}${c.termin_ende ? ` – ${c.termin_ende}` : ""}${c.uhrzeit_von ? `, ${timeLabel(c)} Uhr` : ""})`;
}

export function warnMonteurConflicts(conflicts, name) {
  if (!conflicts.length) return;
  toast.warning(`${name || "Der Monteur"} hat an diesen Tagen bereits ${conflicts.length === 1 ? "einen Einsatz" : `${conflicts.length} Einsätze`}`, {
    description: conflicts.slice(0, 3).map(conflictText).join("\n"),
    duration: 8000,
  });
}

// Custom DayContent renderer — day number + colored dots
export function makeDayContent(categoryMap) {
  return function DayContent({ date }) {
    const key = fmt(date);
    const cats = categoryMap[key];
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <span>{date.getDate()}</span>
        {cats && cats.size > 0 && (
          <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex items-center gap-[2px]">
            {["pv_dc", "pv_ac", "wp", "nacharbeiten"].filter((c) => cats.has(c)).map((c) => (
              <span key={c} className={`inline-block w-1 h-1 rounded-full ${CATEGORY_META[c].color}`} />
            ))}
          </span>
        )}
      </div>
    );
  };
}

export function CategoryLegend({ className = "" }) {
  return (
    <div className={`flex items-center gap-4 text-xs text-muted-foreground ${className}`} data-testid="cal-legend">
      {Object.entries(CATEGORY_META).map(([k, m]) => (
        <div key={k} className="flex items-center gap-1.5">
          <span className={`inline-block w-2 h-2 rounded-full ${m.color}`} />
          <span>{m.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function Projekttermine() {
  const { user } = useAuth();
  const isAuftraggeber = user?.role === "auftraggeber";
  const [projects, setProjects] = useState([]);
  const [monteure, setMonteure] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ projectId: "", phaseKey: "", termin: "", termin_ende: "", uhrzeit_von: "", uhrzeit_bis: "" });
  const [projPickerOpen, setProjPickerOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d; });
  const [calView, setCalView] = useState("month"); // month | week | day | agenda
  const [visibleCats, setVisibleCats] = useState(() => new Set(ALL_CATS));
  const [freie, setFreie] = useState([]);
  const [mode, setMode] = useState("projekt"); // projekt | frei
  const [frei, setFrei] = useState({ id: null, titel: "", datum: "", datum_ende: "", uhrzeit_von: "", uhrzeit_bis: "", monteur_id: "", notiz: "" });
  const [draggingKey, setDraggingKey] = useState(null); // "projectId:phaseKey"
  const [dragOverDay, setDragOverDay] = useState(null);

  const toggleCat = (c) => {
    setVisibleCats((prev) => {
      const n = new Set(prev);
      if (n.has(c)) n.delete(c); else n.add(c);
      return n;
    });
  };

  const load = async () => {
    try {
      const [ps, ms, fs] = await Promise.all([
        api.get("/projects").then((x) => x.data),
        api.get("/monteure").then((x) => x.data).catch(() => []),
        api.get("/kalender-termine").then((x) => x.data).catch(() => []),
      ]);
      setProjects(ps || []);
      setMonteure(ms || []);
      setFreie(fs || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const monteurName = (id) => monteure.find((m) => m.id === id)?.name || monteure.find((m) => m.id === id)?.email;

  const planItems = useMemo(() => [...projects, ...freeTermineAsProjects(freie)], [projects, freie]);
  const termine = useMemo(() => collectProjectTermine(planItems), [planItems]);
  const filteredTermine = useMemo(
    () => termine.filter((t) => visibleCats.has(terminCategory(t))),
    [termine, visibleCats]
  );
  const grouped = useMemo(() => {
    const map = {};
    for (const t of filteredTermine) {
      (map[t.termin] = map[t.termin] || []).push(t);
    }
    return map;
  }, [filteredTermine]);
  const categoryMap = useMemo(() => buildCategoryMap(filteredTermine), [filteredTermine]);
  const DayContent = useMemo(() => makeDayContent(categoryMap), [categoryMap]);

  const dayItems = grouped[fmt(selected)] || [];

  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = fmt(today);
    return filteredTermine.filter((t) => t.termin >= todayStr).slice(0, 20);
  }, [filteredTermine]);

  const openDialogForDate = (d) => {
    setForm({ projectId: "", phaseKey: "", termin: fmt(d), termin_ende: "", uhrzeit_von: "", uhrzeit_bis: "" });
    setFrei({ id: null, titel: "", datum: fmt(d), datum_ende: "", uhrzeit_von: "", uhrzeit_bis: "", monteur_id: "", notiz: "" });
    setMode("projekt");
    setDialogOpen(true);
  };

  const openFreiEdit = (t) => {
    const f = freie.find((x) => `frei-${x.id}` === t.projectId);
    if (!f || isAuftraggeber || user?.role === "monteur") return;
    setFrei({ id: f.id, titel: f.titel, datum: f.datum, datum_ende: f.datum_ende || "", uhrzeit_von: f.uhrzeit_von || "", uhrzeit_bis: f.uhrzeit_bis || "", monteur_id: f.monteur_id || "", notiz: f.notiz || "" });
    setMode("frei");
    setDialogOpen(true);
  };
  // Klick auf einen Eintrag: freie Termine öffnen den Dialog statt des Projekts
  const onItemClick = (e, t) => {
    e.stopPropagation();
    if (t.project_type === "frei") { e.preventDefault(); openFreiEdit(t); }
  };

  const freiConflicts = mode === "frei" && frei.monteur_id
    ? findMonteurConflicts(planItems, frei.monteur_id, frei.datum, frei.datum_ende, frei.id ? `frei-${frei.id}` : null, "frei", frei.uhrzeit_von, frei.uhrzeit_bis)
    : [];

  const saveFrei = async () => {
    if (!frei.titel.trim() || !frei.datum) return;
    if (frei.datum_ende && frei.datum_ende < frei.datum) { toast.error("„bis“ darf nicht vor dem Startdatum liegen"); return; }
    if (frei.uhrzeit_von && frei.uhrzeit_bis && frei.uhrzeit_bis <= frei.uhrzeit_von) { toast.error("Endzeit muss nach der Startzeit liegen"); return; }
    const body = { titel: frei.titel.trim(), datum: frei.datum, datum_ende: frei.datum_ende || null, uhrzeit_von: frei.uhrzeit_von || null, uhrzeit_bis: frei.uhrzeit_von ? frei.uhrzeit_bis || null : null, monteur_id: frei.monteur_id || null, notiz: frei.notiz || "" };
    try {
      if (frei.id) await api.put(`/kalender-termine/${frei.id}`, body);
      else await api.post("/kalender-termine", body);
      toast.success("Termin gespeichert");
      warnMonteurConflicts(freiConflicts, monteurName(frei.monteur_id));
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  };

  const deleteFrei = async () => {
    if (!frei.id || !window.confirm("Termin wirklich löschen?")) return;
    try {
      await api.delete(`/kalender-termine/${frei.id}`);
      toast.success("Termin gelöscht");
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Löschen fehlgeschlagen"); }
  };

  const currentProject = projects.find((p) => p.id === form.projectId);
  const availablePhases = currentProject?.phases || [];
  const selPhase = availablePhases.find((x) => x.key === form.phaseKey);
  const dialogConflicts = selPhase?.monteur_id
    ? findMonteurConflicts(planItems, selPhase.monteur_id, form.termin, form.termin_ende, form.projectId, form.phaseKey, form.uhrzeit_von, form.uhrzeit_bis)
    : [];

  const saveTermin = async (e) => {
    e.preventDefault();
    if (mode === "frei") { saveFrei(); return; }
    if (!form.projectId || !form.phaseKey || !form.termin) return;
    if (form.termin_ende && form.termin_ende < form.termin) { toast.error("„bis“ darf nicht vor dem Startdatum liegen"); return; }
    if (form.uhrzeit_von && form.uhrzeit_bis && form.uhrzeit_bis <= form.uhrzeit_von) { toast.error("Endzeit muss nach der Startzeit liegen"); return; }
    const project = projects.find((p) => p.id === form.projectId);
    if (!project) return;
    const ende = form.termin_ende && form.termin_ende > form.termin ? form.termin_ende : null;
    const zeiten = { uhrzeit_von: form.uhrzeit_von || null, uhrzeit_bis: form.uhrzeit_von ? form.uhrzeit_bis || null : null };
    const newPhases = project.phases.map((ph) =>
      ph.key === form.phaseKey ? { ...ph, termin: form.termin, termin_ende: ende, ...zeiten } : ph
    );
    try {
      await api.patch(`/projects/${form.projectId}`, { phases: newPhases });
      toast.success("Termin gespeichert");
      const ph = project.phases.find((x) => x.key === form.phaseKey);
      warnMonteurConflicts(findMonteurConflicts(planItems, ph?.monteur_id, form.termin, ende, project.id, form.phaseKey, zeiten.uhrzeit_von, zeiten.uhrzeit_bis), monteurName(ph?.monteur_id));
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  };

  // Drag & Drop: Termin von einem Tag auf einen anderen ziehen
  const moveTerminToDate = async (projectId, phaseKey, newDateStr, fromDay) => {
    if (String(projectId).startsWith("frei-")) {
      const f = freie.find((x) => `frei-${x.id}` === projectId);
      if (!f) return;
      const shift = diffDaysStr(fromDay || f.datum, newDateStr);
      if (shift === 0) return;
      const moved = { ...f, datum: addDaysStr(f.datum, shift), datum_ende: f.datum_ende ? addDaysStr(f.datum_ende, shift) : null };
      setFreie((prev) => prev.map((x) => (x.id === f.id ? moved : x)));
      try {
        await api.put(`/kalender-termine/${f.id}`, { titel: moved.titel, datum: moved.datum, datum_ende: moved.datum_ende, uhrzeit_von: moved.uhrzeit_von || null, uhrzeit_bis: moved.uhrzeit_bis || null, monteur_id: moved.monteur_id || null, notiz: moved.notiz || "" });
        toast.success("Termin verschoben");
        warnMonteurConflicts(findMonteurConflicts(planItems, f.monteur_id, moved.datum, moved.datum_ende, projectId, "frei", f.uhrzeit_von, f.uhrzeit_bis), monteurName(f.monteur_id));
      } catch (err) {
        setFreie((prev) => prev.map((x) => (x.id === f.id ? f : x)));
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verschieben fehlgeschlagen");
      }
      return;
    }
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const ph = project.phases.find((x) => x.key === phaseKey);
    if (!ph) return;
    const shift = diffDaysStr(fromDay || ph.termin, newDateStr);
    if (shift === 0) return;
    const moved = { termin: addDaysStr(ph.termin, shift), termin_ende: ph.termin_ende ? addDaysStr(ph.termin_ende, shift) : null };
    newDateStr = moved.termin;
    // Optimistic UI-Update
    const old = { termin: ph.termin, termin_ende: ph.termin_ende || null };
    setProjects((prev) => prev.map((p) => p.id !== projectId ? p : {
      ...p,
      phases: p.phases.map((x) => x.key === phaseKey ? { ...x, ...moved } : x),
    }));
    const newPhases = project.phases.map((x) => x.key === phaseKey ? { ...x, ...moved } : x);
    try {
      await api.patch(`/projects/${projectId}`, { phases: newPhases });
      toast.success(`Termin verschoben auf ${new Date(newDateStr + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}`);
      warnMonteurConflicts(findMonteurConflicts(planItems, ph.monteur_id, moved.termin, moved.termin_ende, projectId, phaseKey, ph.uhrzeit_von, ph.uhrzeit_bis), monteurName(ph.monteur_id));
    } catch (err) {
      // Rollback
      setProjects((prev) => prev.map((p) => p.id !== projectId ? p : {
        ...p,
        phases: p.phases.map((x) => x.key === phaseKey ? { ...x, ...old } : x),
      }));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verschieben fehlgeschlagen");
    }
  };

  return (
    <div className="p-4 lg:p-8 space-y-6 animate-fade-up" data-testid="projekttermine-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline">Projekt-Kalender</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Projekttermine</h1>
          <p className="text-muted-foreground mt-1 text-sm lg:text-base">
            Alle Termine aus Projekt-Phasen auf einen Blick.
          </p>
        </div>
        <Button onClick={() => openDialogForDate(selected)} data-testid="pt-new-termin">
          <Plus className="h-4 w-4 mr-2" /> Neuen Termin setzen
        </Button>
      </div>

      {/* Kategorie-Filter (klickbar) */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="pt-cat-filter">
        <span className="text-xs text-muted-foreground mr-1">Filter:</span>
        <button
          type="button"
          onClick={() => setVisibleCats(new Set(ALL_CATS))}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${visibleCats.size === ALL_CATS.length ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/40"}`}
          data-testid="pt-filter-all"
        >
          Alle
        </button>
        {Object.entries(CATEGORY_META).map(([k, m]) => {
          const active = visibleCats.has(k);
          return (
            <button
              key={k}
              type="button"
              onClick={() => toggleCat(k)}
              className={`text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-muted border-border" : "bg-background border-border opacity-50 hover:opacity-100"}`}
              data-testid={`pt-filter-${k}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${m.color}`} />
              {m.label}
            </button>
          );
        })}
      </div>

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList data-testid="pt-view-tabs">
          <TabsTrigger value="calendar" data-testid="pt-tab-calendar">Kalender</TabsTrigger>
          <TabsTrigger value="list" data-testid="pt-tab-list">Liste</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          {/* Monatsraster mit Terminen direkt in den Zellen */}
          <Card className="border-border">
            <CardContent className="p-3 lg:p-5">
              {/* Ansicht-Sub-Tabs */}
              <div className="flex items-center gap-1 mb-3 border-b border-border" data-testid="pt-cal-view-tabs">
                {[
                  { k: "month", l: "Monat" },
                  { k: "week", l: "Woche" },
                  { k: "day", l: "Tag" },
                  { k: "agenda", l: "Agenda" },
                ].map((v) => (
                  <button
                    key={v.k}
                    type="button"
                    onClick={() => setCalView(v.k)}
                    className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${calView === v.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    data-testid={`pt-cal-view-${v.k}`}
                  >
                    {v.l}
                  </button>
                ))}
              </div>
              {/* Header: Navigation */}
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => {
                    const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); setViewMonth(d); setSelected(new Date());
                  }} data-testid="pt-cal-today">
                    Heute
                  </Button>
                  <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => {
                    if (calView === "week") { const s = new Date(selected); s.setDate(s.getDate() - 7); setSelected(s); const m = new Date(s); m.setDate(1); setViewMonth(m); }
                    else if (calView === "day") { const s = new Date(selected); s.setDate(s.getDate() - 1); setSelected(s); const m = new Date(s); m.setDate(1); setViewMonth(m); }
                    else { const d = new Date(viewMonth); d.setMonth(d.getMonth() - 1); setViewMonth(d); }
                  }} data-testid="pt-cal-prev">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => {
                    if (calView === "week") { const s = new Date(selected); s.setDate(s.getDate() + 7); setSelected(s); const m = new Date(s); m.setDate(1); setViewMonth(m); }
                    else if (calView === "day") { const s = new Date(selected); s.setDate(s.getDate() + 1); setSelected(s); const m = new Date(s); m.setDate(1); setViewMonth(m); }
                    else { const d = new Date(viewMonth); d.setMonth(d.getMonth() + 1); setViewMonth(d); }
                  }} data-testid="pt-cal-next">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="font-heading text-2xl lg:text-3xl font-bold tracking-tight" data-testid="pt-cal-month-label">
                  {calView === "day"
                    ? selected.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
                    : calView === "week"
                    ? (() => { const s = new Date(selected); const dow = (s.getDay()+6)%7; s.setDate(s.getDate()-dow); const e = new Date(s); e.setDate(s.getDate()+6); return `${s.toLocaleDateString("de-DE",{day:"2-digit",month:"long"})} – ${e.toLocaleDateString("de-DE",{day:"2-digit",month:"long",year:"numeric"})}`; })()
                    : calView === "agenda"
                    ? "Agenda"
                    : viewMonth.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
                </div>
                <Button size="sm" onClick={() => openDialogForDate(selected)} data-testid="pt-cal-add" style={{ display: isAuftraggeber ? "none" : undefined }}>
                  <Plus className="h-4 w-4 mr-1.5" /> Neuer Termin
                </Button>
              </div>

              {calView === "month" && (
                <>
                  {/* Wochentag-Kopfzeile */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {["Mo","Di","Mi","Do","Fr","Sa","So"].map((wd) => (
                      <div key={wd} className="text-center text-[11px] uppercase tracking-wider text-muted-foreground font-semibold py-1.5">
                        {wd}
                      </div>
                    ))}
                  </div>

                  {/* Tages-Zellen */}
                  <div className="grid grid-cols-7 gap-1" data-testid="pt-month-grid">
                    {(() => {
                      const first = new Date(viewMonth);
                      first.setDate(1);
                      const startOffset = (first.getDay() + 6) % 7;
                      const gridStart = new Date(first);
                      gridStart.setDate(1 - startOffset);
                      const cells = [];
                      const todayStr = fmt(new Date());
                      for (let i = 0; i < 42; i++) {
                        const d = new Date(gridStart);
                        d.setDate(gridStart.getDate() + i);
                        const key = fmt(d);
                        const inMonth = d.getMonth() === viewMonth.getMonth();
                        const isToday = key === todayStr;
                        const isSelected = key === fmt(selected);
                        const items = grouped[key] || [];
                        const isDropTarget = dragOverDay === key;
                        cells.push(
                          <div
                            key={key}
                            onClick={() => { setSelected(new Date(d)); if (!isAuftraggeber) openDialogForDate(d); }}
                            onDragOver={(e) => { if (draggingKey) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverDay !== key) setDragOverDay(key); } }}
                            onDragLeave={() => setDragOverDay((k) => (k === key ? null : k))}
                            onDrop={(e) => {
                              e.preventDefault();
                              setDragOverDay(null);
                              const raw = e.dataTransfer.getData("application/json");
                              setDraggingKey(null);
                              if (!raw) return;
                              try { const { projectId, phaseKey, day } = JSON.parse(raw); moveTerminToDate(projectId, phaseKey, key, day); } catch {}
                            }}
                            className={[
                              "relative min-h-[110px] lg:min-h-[130px] rounded-md border p-1.5 cursor-pointer transition-colors overflow-hidden",
                              inMonth ? "bg-background" : "bg-muted/30 text-muted-foreground/60",
                              isDropTarget ? "border-primary ring-2 ring-primary/50 bg-primary/5" : (isSelected ? "border-primary ring-1 ring-primary/40" : "border-border hover:border-primary/40"),
                              isToday && !isSelected && !isDropTarget ? "border-primary/50" : "",
                            ].join(" ")}
                            data-testid={`pt-cell-${key}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-semibold ${isToday ? "inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground" : ""}`}>
                                {d.getDate()}
                              </span>
                            </div>
                            <div className="space-y-0.5">
                              {items.map((t, i) => {
                                const cat = terminCategory(t);
                                const bg = CAT_BG[cat];
                                const prefix = cat === "pv_dc" ? "DC" : cat === "pv_ac" ? "AC" : cat === "nacharbeiten" ? "NA" : cat === "frei" ? "•" : "WP";
                                const dragId = `${t.projectId}:${t.phase_key}`;
                                return (
                                  <Link
                                    key={`${t.projectId}-${t.phase_key}-${i}`}
                                    to={`/projekte?open=${t.projectId}`}
                                    state={{ from: "/projekttermine" }}
                                    draggable
                                    onDragStart={(e) => {
                                      e.stopPropagation();
                                      e.dataTransfer.setData("application/json", JSON.stringify({ projectId: t.projectId, phaseKey: t.phase_key, day: t.termin }));
                                      e.dataTransfer.effectAllowed = "move";
                                      setDraggingKey(dragId);
                                    }}
                                    onDragEnd={() => { setDraggingKey(null); setDragOverDay(null); }}
                                    onClick={(e) => onItemClick(e, t)}
                                    className={`block truncate text-[10px] leading-tight px-1.5 py-1 rounded border cursor-grab active:cursor-grabbing ${bg} hover:brightness-95 ${draggingKey === dragId ? "opacity-40" : ""}`}
                                    title={`${prefix} ${t.kunde_name} · ${t.phase_label}${t.monteur_id ? " · " + (monteurName(t.monteur_id) || "Monteur") : ""} — Drag & Drop zum Verschieben`}
                                    data-testid={`pt-cell-item-${t.projectId}-${t.phase_key}`}
                                  >
                                    <span className="font-semibold">{prefix}</span> {t.uhrzeit_von && <span className="font-mono opacity-75">{t.uhrzeit_von} </span>}{t.kunde_plz && <span className="font-mono opacity-75" data-testid={`pt-cell-plz-${t.projectId}-${t.phase_key}`}>{t.kunde_plz} </span>}{t.kunde_name}
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      return cells;
                    })()}
                  </div>
                </>
              )}

              {calView === "week" && (
                <div className="space-y-1" data-testid="pt-week-wrapper">
                {(() => {
                  const ws = new Date(selected);
                  ws.setDate(ws.getDate() - ((ws.getDay() + 6) % 7));
                  const wsKey = fmt(ws);
                  const weKey = addDaysStr(wsKey, 6);
                  const seen = new Set();
                  const bars = [];
                  for (const t of filteredTermine) {
                    if (!t.termin_ende) continue;
                    const id = `${t.projectId}:${t.phase_key}`;
                    if (seen.has(id) || t.termin_ende < wsKey || t.termin_start > weKey) continue;
                    seen.add(id);
                    bars.push({ ...t, s: Math.max(0, diffDaysStr(wsKey, t.termin_start)), e: Math.min(6, diffDaysStr(wsKey, t.termin_ende)) });
                  }
                  if (!bars.length) return null;
                  return (
                    <div className="grid grid-cols-7 gap-1" data-testid="pt-week-multiday">
                      {bars.map((t) => {
                        const dragId = `${t.projectId}:${t.phase_key}`;
                        const label = `${t.kunde_plz ? `${t.kunde_plz} ` : ""}${t.kunde_name.replace(/ \(\d+\/\d+\)$/, "")}`;
                        return (
                          <Link
                            key={dragId}
                            to={`/projekte?open=${t.projectId}`}
                            state={{ from: "/projekttermine" }}
                            draggable
                            onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("application/json", JSON.stringify({ projectId: t.projectId, phaseKey: t.phase_key, day: addDaysStr(wsKey, t.s) })); e.dataTransfer.effectAllowed = "move"; setDraggingKey(dragId); }}
                            onDragEnd={() => { setDraggingKey(null); setDragOverDay(null); }}
                            onClick={(e) => onItemClick(e, t)}
                            style={{ gridColumn: `${t.s + 1} / ${t.e + 2}` }}
                            className={`flex items-center gap-2 text-[11px] px-2 py-1.5 border cursor-grab active:cursor-grabbing hover:brightness-95 ${CAT_BG[terminCategory(t)]} ${t.termin_start < wsKey ? "rounded-l-none border-l-0" : "rounded-l-md"} ${t.termin_ende > weKey ? "rounded-r-none border-r-0" : "rounded-r-md"} ${draggingKey === dragId ? "opacity-40" : ""}`}
                            title={`${label} · ${t.phase_label} · ${t.termin_start} – ${t.termin_ende}`}
                            data-testid={`pt-week-bar-${t.projectId}-${t.phase_key}`}
                          >
                            <span className="font-semibold truncate">{label}</span>
                            {t.uhrzeit_von && <span className="font-mono opacity-75 shrink-0">{timeLabel(t)}</span>}
                            <span className="opacity-80 truncate">{t.phase_label}</span>
                            {t.monteur_id && <span className="opacity-70 truncate hidden md:inline">· {monteurName(t.monteur_id) || "Monteur"}</span>}
                            <span className="ml-auto font-mono opacity-60 shrink-0 hidden lg:inline">{t.termin_start.slice(8)}.–{t.termin_ende.slice(8)}.{t.termin_ende.slice(5, 7)}.</span>
                          </Link>
                        );
                      })}
                    </div>
                  );
                })()}
                <div className="grid grid-cols-7 gap-1" data-testid="pt-week-grid">
                  {(() => {
                    const start = new Date(selected);
                    const dow = (start.getDay() + 6) % 7; // Mo=0
                    start.setDate(start.getDate() - dow);
                    start.setHours(0,0,0,0);
                    const todayStr = fmt(new Date());
                    return Array.from({ length: 7 }).map((_, i) => {
                      const d = new Date(start);
                      d.setDate(start.getDate() + i);
                      const key = fmt(d);
                      const isToday = key === todayStr;
                      const isSelected = key === fmt(selected);
                      const items = (grouped[key] || []).filter((t) => !t.termin_ende);
                      const isDropTarget = dragOverDay === key;
                      return (
                        <div
                          key={key}
                          onClick={() => { setSelected(new Date(d)); if (!isAuftraggeber) openDialogForDate(d); }}
                          onDragOver={(e) => { if (draggingKey) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverDay !== key) setDragOverDay(key); } }}
                          onDragLeave={() => setDragOverDay((k) => (k === key ? null : k))}
                          onDrop={(e) => { e.preventDefault(); setDragOverDay(null); const raw = e.dataTransfer.getData("application/json"); setDraggingKey(null); if (!raw) return; try { const { projectId, phaseKey, day } = JSON.parse(raw); moveTerminToDate(projectId, phaseKey, key, day); } catch {} }}
                          className={[
                            "min-h-[400px] rounded-md border p-2 cursor-pointer transition-colors",
                            isDropTarget ? "border-primary ring-2 ring-primary/50 bg-primary/5" : isSelected ? "border-primary ring-1 ring-primary/40" : "border-border hover:border-primary/40",
                          ].join(" ")}
                          data-testid={`pt-week-cell-${key}`}
                        >
                          <div className="text-center mb-2 pb-2 border-b border-border">
                            <div className="text-[10px] uppercase text-muted-foreground tracking-wider">
                              {d.toLocaleDateString("de-DE", { weekday: "short" })}
                            </div>
                            <div className={`text-lg font-bold ${isToday ? "inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary text-primary-foreground" : ""}`}>
                              {d.getDate()}
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            {items.length === 0 ? (
                              <div className="text-[10px] text-muted-foreground italic text-center py-2">—</div>
                            ) : items.map((t, i) => {
                              const cat = terminCategory(t);
                              const bg = CAT_BG[cat];
                              const dragId = `${t.projectId}:${t.phase_key}`;
                              return (
                                <Link
                                  key={`${t.projectId}-${t.phase_key}-${i}`}
                                  to={`/projekte?open=${t.projectId}`}
                                  state={{ from: "/projekttermine" }}
                                  draggable
                                  onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("application/json", JSON.stringify({ projectId: t.projectId, phaseKey: t.phase_key, day: t.termin })); e.dataTransfer.effectAllowed = "move"; setDraggingKey(dragId); }}
                                  onDragEnd={() => { setDraggingKey(null); setDragOverDay(null); }}
                                  onClick={(e) => onItemClick(e, t)}
                                  className={`block text-[11px] p-1.5 rounded border cursor-grab active:cursor-grabbing ${bg} hover:brightness-95 ${draggingKey === dragId ? "opacity-40" : ""}`}
                                  data-testid={`pt-week-item-${t.projectId}-${t.phase_key}`}
                                >
                                  {t.uhrzeit_von && <div className="text-[10px] font-mono opacity-80" data-testid={`pt-week-time-${t.projectId}-${t.phase_key}`}>{timeLabel(t)}</div>}
                                  <div className="font-semibold truncate">{t.kunde_plz && <span className="font-mono font-normal opacity-75">{t.kunde_plz} </span>}{t.kunde_name}</div>
                                  <div className="text-[10px] opacity-80 truncate">{t.phase_label}</div>
                                  {t.monteur_id && <div className="text-[10px] opacity-70 truncate mt-0.5">{monteurName(t.monteur_id) || "Monteur"}</div>}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                </div>
              )}

              {calView === "day" && (
                <div className="space-y-2" data-testid="pt-day-view">
                  {dayItems.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border rounded-md">
                      Keine Projekttermine an diesem Tag. Klick oben auf „Neuer Termin".
                    </div>
                  ) : dayItems.map((t, i) => {
                    const cat = terminCategory(t);
                    const Icon = CATEGORY_META[cat].icon;
                    return (
                      <Link
                        key={`${t.projectId}-${t.phase_key}-${i}`}
                        to={`/projekte?open=${t.projectId}`}
                        state={{ from: "/projekttermine" }}
                        onClick={(e) => onItemClick(e, t)}
                        className="block border border-border rounded-md p-4 hover:border-primary/60 transition-colors"
                        data-testid={`pt-day-view-item-${t.projectId}-${t.phase_key}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex items-start gap-3">
                            <span className={`inline-block w-3 h-3 rounded-full mt-1.5 shrink-0 ${CATEGORY_META[cat].color}`} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Icon className={`h-5 w-5 shrink-0 ${cat === "pv_dc" ? "text-emerald-500" : cat === "pv_ac" ? "text-red-500" : cat === "nacharbeiten" ? "text-orange-500" : "text-purple-500"}`} />
                                {t.uhrzeit_von && <span className="font-mono text-sm text-primary shrink-0">{timeLabel(t)} Uhr</span>}
                                <span className="font-semibold text-lg truncate">{t.kunde_plz && <span className="font-mono font-normal text-muted-foreground">{t.kunde_plz} </span>}{t.kunde_name}</span>
                              </div>
                              <div className="text-sm text-muted-foreground mt-0.5 truncate">
                                {t.phase_label} {t.kunde_adresse && `· ${t.kunde_adresse}`}
                              </div>
                              {t.monteur_id && (
                                <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary">
                                  <UserIcon className="h-3.5 w-3.5" /> {monteurName(t.monteur_id) || "Monteur"}
                                </div>
                              )}
                            </div>
                          </div>
                          <span className={`shrink-0 px-2.5 py-1 rounded text-xs ${PHASE_STATUS_CLS[t.status]}`}>
                            {PHASE_STATUS_LBL[t.status]}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}

              {calView === "agenda" && (
                <div className="space-y-4" data-testid="pt-agenda-view">
                  {(() => {
                    if (upcoming.length === 0) {
                      return <div className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border rounded-md">Keine anstehenden Termine.</div>;
                    }
                    const byDate = {};
                    for (const t of upcoming) { (byDate[t.termin] = byDate[t.termin] || []).push(t); }
                    return Object.entries(byDate).map(([date, items]) => (
                      <div key={date} data-testid={`pt-agenda-group-${date}`}>
                        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border pb-1.5 mb-2">
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">
                            {new Date(date + "T00:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                          </div>
                        </div>
                        <div className="space-y-2">
                          {items.map((t, i) => {
                            const cat = terminCategory(t);
                            const Icon = CATEGORY_META[cat].icon;
                            return (
                              <Link
                                key={`${t.projectId}-${t.phase_key}-${i}`}
                                to={`/projekte?open=${t.projectId}`}
                                state={{ from: "/projekttermine" }}
                                onClick={(e) => onItemClick(e, t)}
                                className="block border border-border rounded-md p-3 hover:border-primary/60 transition-colors"
                                data-testid={`pt-agenda-item-${t.projectId}-${t.phase_key}`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 flex items-start gap-2">
                                    <span className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${CATEGORY_META[cat].color}`} />
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2">
                                        <Icon className={`h-4 w-4 shrink-0 ${cat === "pv_dc" ? "text-emerald-500" : cat === "pv_ac" ? "text-red-500" : cat === "nacharbeiten" ? "text-orange-500" : "text-purple-500"}`} />
                                        {t.uhrzeit_von && <span className="font-mono text-xs text-primary shrink-0">{timeLabel(t)}</span>}
                                        <span className="font-medium truncate">{t.kunde_plz && <span className="font-mono font-normal text-muted-foreground">{t.kunde_plz} </span>}{t.kunde_name}</span>
                                      </div>
                                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                        {t.phase_label} {t.kunde_adresse && `· ${t.kunde_adresse}`}
                                      </div>
                                    </div>
                                  </div>
                                  <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] ${PHASE_STATUS_CLS[t.status]}`}>{PHASE_STATUS_LBL[t.status]}</span>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}

              {calView === "month" && (
              /* Detail für ausgewählten Tag (nur Monatsansicht) */
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <div>
                    <div className="overline">Termine am</div>
                    <div className="font-heading text-lg font-bold mt-0.5">
                      {selected.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openDialogForDate(selected)} data-testid="pt-add-for-day" style={{ display: isAuftraggeber ? "none" : undefined }}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Termin hinzufügen
                  </Button>
                </div>
                <div className="space-y-2">
                  {dayItems.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-4" data-testid="pt-no-day">
                      Keine Projekttermine an diesem Tag.
                    </div>
                  ) : dayItems.map((t, i) => {
                    const cat = terminCategory(t);
                    const Icon = CATEGORY_META[cat].icon;
                    return (
                      <Link
                        key={`${t.projectId}-${t.phase_key}-${i}`}
                        to={`/projekte?open=${t.projectId}`}
                        state={{ from: "/projekttermine" }}
                        onClick={(e) => onItemClick(e, t)}
                        className="block border border-border rounded-md p-3 hover:border-primary/60 transition-colors"
                        data-testid={`pt-day-${t.projectId}-${t.phase_key}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex items-start gap-2">
                            <span className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${CATEGORY_META[cat].color}`} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Icon className={`h-4 w-4 shrink-0 ${cat === "pv_dc" ? "text-emerald-500" : cat === "pv_ac" ? "text-red-500" : cat === "nacharbeiten" ? "text-orange-500" : "text-purple-500"}`} />
                                {t.uhrzeit_von && <span className="font-mono text-xs text-primary shrink-0" data-testid={`pt-day-time-${t.projectId}-${t.phase_key}`}>{timeLabel(t)}</span>}
                                <span className="font-medium truncate">{t.kunde_plz && <span className="font-mono font-normal text-muted-foreground">{t.kunde_plz} </span>}{t.kunde_name}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                {t.phase_label} {t.kunde_adresse && `· ${t.kunde_adresse}`}
                              </div>
                              {t.monteur_id && (
                                <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary" data-testid={`pt-monteur-${t.projectId}-${t.phase_key}`}>
                                  <UserIcon className="h-3 w-3" /> {monteurName(t.monteur_id) || "Monteur"}
                                </div>
                              )}
                            </div>
                          </div>
                          <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] ${PHASE_STATUS_CLS[t.status]}`}>
                            {PHASE_STATUS_LBL[t.status]}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="list">
          <Card className="border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Kunde</TableHead>
                    <TableHead>Phase</TableHead>
                    <TableHead>Monteur</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {termine.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground" data-testid="pt-empty">
                      Noch keine Projekttermine. Klick oben auf „Neuen Termin setzen".
                    </TableCell></TableRow>
                  ) : termine.map((t, i) => {
                    const cat = terminCategory(t);
                    return (
                      <TableRow key={`${t.projectId}-${t.phase_key}-${i}`} data-testid={`pt-row-${t.projectId}-${t.phase_key}`}>
                        <TableCell className="font-mono text-xs">{t.termin}{t.uhrzeit_von ? ` ${timeLabel(t)}` : ""}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <span className={`inline-block w-2 h-2 rounded-full ${CATEGORY_META[cat].color}`} />
                            {CATEGORY_META[cat].label}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          <Link to={`/projekte?open=${t.projectId}`} state={{ from: "/projekttermine" }} onClick={(e) => onItemClick(e, t)} className="hover:text-primary hover:underline">{t.kunde_name}</Link>
                        </TableCell>
                        <TableCell className="text-sm">{t.phase_label}</TableCell>
                        <TableCell className="text-sm">
                          {t.monteur_id ? (
                            <span className="inline-flex items-center gap-1 text-xs">
                              <UserIcon className="h-3 w-3 text-primary" /> {monteurName(t.monteur_id) || "—"}
                            </span>
                          ) : <span className="text-muted-foreground text-xs">—</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${PHASE_STATUS_CLS[t.status]}`}>{PHASE_STATUS_LBL[t.status]}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {upcoming.length > 0 && (
            <Card className="border-border mt-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CalendarClock className="h-4 w-4 text-primary" />
                  <span><span className="font-medium text-foreground">{upcoming.length}</span> anstehende Termine ab heute</span>
                  <Link to="/projekte" className="ml-auto text-xs text-primary hover:underline inline-flex items-center gap-1">
                    Zu den Projekten <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Termin setzen Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="pt-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" /> {mode === "frei" && frei.id ? "Termin bearbeiten" : "Termin setzen"}
            </DialogTitle>
            <DialogDescription>
              {mode === "frei" ? "Termin ohne Projekt — mit Titel, Monteur und Notiz." : "Wähle Projekt und Phase — der Termin wird direkt am Projekt gespeichert."}
            </DialogDescription>
          </DialogHeader>
          {!frei.id && (
            <div className="inline-flex rounded-md border border-border p-0.5 text-sm" data-testid="pt-mode-toggle">
              {[["projekt", "Mit Projekt"], ["frei", "Ohne Projekt"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => { setMode(k); if (k === "frei") setFrei((f) => ({ ...f, datum: f.datum || form.termin, datum_ende: f.datum_ende || form.termin_ende })); }}
                  className={`px-3 py-1 rounded transition-colors ${mode === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`pt-mode-${k}`}>{l}</button>
              ))}
            </div>
          )}
          <form onSubmit={saveTermin} className="space-y-4">
            {mode === "frei" ? (
              <FreeTerminForm value={frei} onChange={setFrei} monteure={monteure} />
            ) : (<>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Datum *</Label>
                <Input type="date" value={form.termin} onChange={(e) => setForm({ ...form, termin: e.target.value })} required data-testid="pt-datum" />
              </div>
              <div className="space-y-1.5">
                <Label>bis (optional)</Label>
                <Input type="date" value={form.termin_ende} onChange={(e) => setForm({ ...form, termin_ende: e.target.value })} data-testid="pt-datum-ende" />
              </div>
              <div className="space-y-1.5">
                <Label>Uhrzeit von</Label>
                <Input type="time" value={form.uhrzeit_von} onChange={(e) => setForm({ ...form, uhrzeit_von: e.target.value })} data-testid="pt-zeit-von" />
              </div>
              <div className="space-y-1.5">
                <Label>Uhrzeit bis</Label>
                <Input type="time" value={form.uhrzeit_bis} disabled={!form.uhrzeit_von} onChange={(e) => setForm({ ...form, uhrzeit_bis: e.target.value })} data-testid="pt-zeit-bis" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Projekt *</Label>
              <Popover open={projPickerOpen} onOpenChange={setProjPickerOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="pt-projekt">
                    <span className={`truncate ${currentProject ? "" : "text-muted-foreground"}`}>
                      {currentProject ? `${currentProject.project_type === "pv" ? "☀ " : "🔥 "}${currentProject.kunde_name}` : "Projekt wählen …"}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <Command filter={(value, search) => {
                    const v = value.toLowerCase();
                    return search.toLowerCase().split(/\s+/).filter(Boolean).every((w) => v.includes(w)) ? 1 : 0;
                  }}>
                    <CommandInput placeholder="Kunde, Ort oder Typ suchen …" data-testid="pt-projekt-search" />
                    <CommandList className="max-h-64">
                      <CommandEmpty>Kein Projekt gefunden.</CommandEmpty>
                      {projects.map((p) => (
                        <CommandItem key={p.id}
                          value={`${p.kunde_name} ${p.kunde_ort || ""} ${p.kunde_plz || ""} ${p.project_type === "pv" ? "PV Photovoltaik" : "Wärmepumpe WP"} ${p.id}`}
                          onSelect={() => { setForm({ ...form, projectId: p.id, phaseKey: "" }); setProjPickerOpen(false); }}
                          data-testid={`pt-projekt-option-${p.id}`}>
                          <span className="truncate">{p.project_type === "pv" ? "☀ " : "🔥 "}{p.kunde_name}</span>
                          {p.kunde_ort && <span className="ml-auto text-xs text-muted-foreground shrink-0">{p.kunde_ort}</span>}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label>Phase *</Label>
              <Select value={form.phaseKey} onValueChange={(v) => {
                const ph = availablePhases.find((x) => x.key === v);
                setForm({ ...form, phaseKey: v, termin_ende: form.termin_ende || ph?.termin_ende || "", uhrzeit_von: ph?.uhrzeit_von || "", uhrzeit_bis: ph?.uhrzeit_bis || "" });
              }} disabled={!form.projectId}>
                <SelectTrigger data-testid="pt-phase"><SelectValue placeholder={form.projectId ? "Phase wählen …" : "Erst Projekt wählen"} /></SelectTrigger>
                <SelectContent>
                  {availablePhases.map((ph) => (
                    <SelectItem key={ph.key} value={ph.key}>
                      {ph.label} {ph.termin && `(aktuell: ${ph.termin}${ph.termin_ende ? ` – ${ph.termin_ende}` : ""})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {dialogConflicts.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 text-xs space-y-1" data-testid="pt-monteur-conflict">
                <div className="font-medium text-amber-800 dark:text-amber-300">
                  {monteurName(selPhase.monteur_id) || "Der Monteur"} hat an diesen Tagen bereits einen Einsatz:
                </div>
                {dialogConflicts.map((c, i) => <div key={i}>{conflictText(c)}</div>)}
              </div>
            )}
            </>)}
            {freiConflicts.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 text-xs space-y-1" data-testid="pt-frei-conflict">
                <div className="font-medium text-amber-800 dark:text-amber-300">
                  {monteurName(frei.monteur_id) || "Der Monteur"} hat an diesen Tagen bereits einen Einsatz:
                </div>
                {freiConflicts.map((c, i) => <div key={i}>{conflictText(c)}</div>)}
              </div>
            )}
            <DialogFooter className="gap-2">
              {mode === "frei" && frei.id && (
                <Button type="button" variant="ghost" className="text-destructive mr-auto" onClick={deleteFrei} data-testid="pt-frei-delete">Löschen</Button>
              )}
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit" disabled={mode === "frei" ? !frei.titel.trim() || !frei.datum : !form.projectId || !form.phaseKey || !form.termin} data-testid="pt-submit">Speichern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
